import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchGames } from "../store/slices/gameSlice";
import { Icon } from "@iconify/react";
import GameCard from "../components/GameCard";
import GameLaunchOverlay from "../components/GameLaunchOverlay";
import GamePaginationControls from "../components/GamePaginationControls";
import {
  createRestrictionState,
  getBlockedTypeMessage,
  getRestrictionSummary,
} from "../utils/gameRestrictions";
import { refreshUserLaunchState } from "../utils/refreshUserLaunchState";
import { buildGameLaunchPayload, fetchGameUrl } from "../utils/gameLauncher";
import {
  createProviderGamesPagination,
  DEFAULT_PROVIDER_GAMES_LIMIT,
  fetchProviderGames,
} from "../utils/providerGames";
import { resolveProviderImage } from "../utils/providerImage";
import { filterVisibleProvidersByCategory } from "../utils/providerVisibility";

const gameListData = [
  "bs",
  "ng",
  "aws",
  "swg",
  "km",
  "fc",
  "mg",
  "dgs",
  "gamatron",
  "ep",
  "jl",
  "sn",
  "pp",
  "kg",
  "netent",
  "joker",
  "bpg",
  "bng",
  "hab",
  "kgl",
  "rlx",
  "ygg",
  "red",
  "qs",
  "ids",
  "tk",
  "ds",
  "nlc",
  "ga",
  "png",
  "pug",
  "fng",
  "nge",
  "1x2",
  "hak",
  "waz",
  "elk",
  "prs",
  "dgs",
];

// Beautiful Toast Notification Component
const Toast = ({ message, type = "warning", show, onClose }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [show, onClose]);

  if (!show) return null;

  const getIcon = () => {
    switch (type) {
      case "info":
        return "ℹ️";
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      default:
        return "⚠️";
    }
  };

  const getColors = () => {
    switch (type) {
      case "info":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "warning":
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
      case "error":
        return {
          bg: "#FEE2E2",
          border: "#EF4444",
          text: "#991B1B",
        };
      default:
        return {
          bg: "#FEF3C7",
          border: "#F59E0B",
          text: "#92400E",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        right: "20px",
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        borderRadius: "12px",
        padding: "16px 20px",
        maxWidth: "400px",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
        zIndex: 10000,
        animation: "slideIn 0.3s ease-out",
        display: "flex",
        alignItems: "center",
        gap: "12px",
      }}
    >
      <span style={{ fontSize: "20px" }}>{getIcon()}</span>
      <span style={{ color: colors.text, fontWeight: "500", flex: 1 }}>
        {message}
      </span>
      <button
        onClick={onClose}
        style={{
          background: "none",
          border: "none",
          fontSize: "24px",
          cursor: "pointer",
          color: colors.text,
          padding: "4px",
          marginLeft: "8px",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "rgba(0, 0, 0, 0.1)";
          e.currentTarget.style.transform = "scale(1.1)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        ×
      </button>
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

let slotGamesFetched = false;

const Slot = () => {
  // Toast notification state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "warning",
  });

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  const dispatch = useDispatch();
  const { gameList } = useSelector((state) => state.games);
  const { singleUsers } = useSelector((state) => state.user);

  const currentUser = singleUsers && singleUsers.length ? singleUsers[0] : null;
  const restrictionState = createRestrictionState(currentUser);
  const isTypeAllowed = restrictionState.isTypeAllowed("slot");
  const isProviderAllowed = restrictionState.isProviderAllowed;
  const visibleProviders = useMemo(
    () =>
      filterVisibleProvidersByCategory(
        Array.isArray(gameList?.slots) ? gameList.slots : [],
        "SLOTS",
      ),
    [gameList],
  );
  const restrictionSummary = getRestrictionSummary(
    currentUser,
    visibleProviders,
  );

  const loadProviderGamesWithCheck = (provider) => {
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }
    if (!isTypeAllowed) {
      showToast(getBlockedTypeMessage("slot"), "warning");
      return;
    }
    if (isProviderAllowed(provider)) {
      loadProviderGames(provider);
    } else {
      showToast(
        "ท่านสามารถเล่นได้บางประเภท,บางค่ายเกมส์เท่านั้น เนื่องจากติดเงื่อนไข",
        "warning",
      );
    }
  };

  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerGames, setProviderGames] = useState([]);
  const [providerGamesPagination, setProviderGamesPagination] = useState(() =>
    createProviderGamesPagination(),
  );
  const [isLoadingGames, setIsLoadingGames] = useState(false);
  const [gamesError, setGamesError] = useState(null);
  const [gameStartLoading, setGameStartLoading] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [showGameSearch, setShowGameSearch] = useState(false);
  const [gameQuery, setGameQuery] = useState("");
  const playerBalance = useSelector((state) => state.user.playerBalance);

  useEffect(() => {
    if (slotGamesFetched) return;
    slotGamesFetched = true;
    dispatch(fetchGames());
  }, [dispatch]);

  const updateSuggestions = (val) => {
    setQuery(val);
    const q = (val || "").toLowerCase();
    setSuggestions(
      q
        ? visibleProviders
            .filter((p) =>
              ((p.productName || p.name || "") + "").toLowerCase().includes(q),
            )
            .slice(0, 10)
        : [],
    );
  };

  const selectSuggestion = (provider) => {
    setShowSearch(false);
    setQuery("");
    setSuggestions([]);
    // require login before loading provider games
    if (!currentUser) {
      showToast("กรุณาเข้าสู่ระบบก่อนเข้าเล่น", "info");
      return;
    }
    loadProviderGames(provider);
  };

  const loadProviderGames = async (provider, page = 1) => {
    setSelectedProvider(provider);
    setIsLoadingGames(true);
    setGamesError(null);
    setGameQuery("");
    setShowGameSearch(false);
    setShowSearch(false);
    setQuery("");
    setSuggestions([]);

    try {
      const result = await fetchProviderGames({
        apiBaseUrl: API_BASE_URL,
        provider,
        categoryCode: "SLOTS",
        page,
        limit: providerGamesPagination.limit || DEFAULT_PROVIDER_GAMES_LIMIT,
      });
      setProviderGames(result.games);
      setProviderGamesPagination(result.pagination);
    } catch (err) {
      console.error(err);
      setGamesError("ไม่สามารถโหลดรายการเกมได้");
      setProviderGames([]);
      setProviderGamesPagination(
        createProviderGamesPagination(
          providerGamesPagination.limit || DEFAULT_PROVIDER_GAMES_LIMIT,
        ),
      );
    } finally {
      setIsLoadingGames(false);
    }
  };

  const backToProviders = () => {
    setSelectedProvider(null);
    setProviderGames([]);
    setProviderGamesPagination(createProviderGamesPagination());
    setGamesError(null);
    setGameQuery("");
    setShowGameSearch(false);
    // Ensure provider-level search input closes when returning to provider list
    setShowSearch(false);
    setQuery("");
    setSuggestions([]);
  };

  const getGameLink = async (request) => {
    setGameStartLoading(true);

    await refreshUserLaunchState(dispatch, currentUser);

    const launchData = buildGameLaunchPayload({
      game: request,
      selectedProvider,
    });

    try {
      const gameUrl = await fetchGameUrl({
        apiBaseUrl: API_BASE_URL,
        launchData,
        playerBalance,
      });
      window.location.href = gameUrl;
    } catch (err) {
      console.error(err);
      alert("เกิดข้อผิดพลาดในการเข้าสู่เกมส์");
    } finally {
      setGameStartLoading(false);
    }
  };

  const filteredProviderGames = useMemo(() => {
    if (!gameQuery) return providerGames;
    const q = gameQuery.toLowerCase();
    return (providerGames || []).filter((g) => {
      const name = (g.gameName || g.name || g.title || "").toLowerCase();
      return name.includes(q);
    });
  }, [providerGames, gameQuery]);

  const handleGamesPageChange = (page) => {
    if (
      !selectedProvider ||
      page === providerGamesPagination.page ||
      page < 1
    ) {
      return;
    }

    loadProviderGames(selectedProvider, page);
  };

  return (
    <div
      className="games-page-shell"
      style={{
        padding: "18px 0 40px",
        marginTop: 8,
        minHeight: "100vh",
        background: "transparent",
      }}
    >
      <div
        style={{
          margin: "0 8px",
          padding: "24px 16px",
          border: "1px solid #b7e3d8",
          borderRadius: "18px",
          background: "rgba(233, 247, 244, 0.85)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="page-container">
          <div
            className="header-section"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            {selectedProvider ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 12,
                }}
              >
                <button
                  className="back-button"
                  onClick={backToProviders}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    background: "rgba(255,255,255,0.06)",
                    border: "1.5px solid var(--primaryColor)",
                    color: "var(--primaryColor)",
                    borderRadius: 25,
                    padding: "8px 18px 8px 12px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    fontSize: "14px",
                    fontWeight: 600,
                    minHeight: "40px",
                    transition: "all 0.25s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--primaryColor)";
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                    e.currentTarget.style.color = "var(--primaryColor)";
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  กลับไปหน้าผู้ให้บริการ
                </button>

                {showGameSearch ? (
                  <div
                    style={{
                      position: "relative",
                      flex: "0 1 320px",
                      minWidth: 0,
                    }}
                  >
                    <input
                      autoFocus
                      value={gameQuery}
                      onChange={(e) => setGameQuery(e.target.value)}
                      placeholder="ค้นหาเกม..."
                      style={{
                        width: "100%",
                        padding: "9px 36px 9px 14px",
                        borderRadius: 24,
                        border: "1.5px solid var(--primaryColor)",
                        outline: "none",
                        background: "#1a1a1a",
                        color: "#fff",
                        fontSize: 14,
                        boxSizing: "border-box",
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setShowGameSearch(false);
                          setGameQuery("");
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        setShowGameSearch(false);
                        setGameQuery("");
                      }}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#999",
                        cursor: "pointer",
                        padding: 4,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M18 6L6 18M6 6l12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="search-button"
                    onClick={() => setShowGameSearch(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      background: "var(--primaryColor)",
                      border: "none",
                      borderRadius: 25,
                      padding: "8px 18px 8px 14px",
                      cursor: "pointer",
                      color: "#fff",
                      fontSize: 14,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      minHeight: "40px",
                      boxShadow:
                        "0 2px 8px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                      transition: "all 0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = "0.85";
                      e.currentTarget.style.transform = "translateY(-1px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 14px color-mix(in srgb, var(--primaryColor) 40%, transparent)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = "1";
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M21 21L16.514 16.506M19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    ค้นหา
                  </button>
                )}
              </div>
            ) : showSearch ? (
              <></>
            ) : (
              <></>
            )}
          </div>

          {!selectedProvider && (
            <div>
              {/* Alert for Restricted Game Categories */}
              {!isTypeAllowed && (
                <div style={{ marginBottom: 24 }}>
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
                      borderRadius: "16px",
                      padding: "16px 24px",
                      marginBottom: 16,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
                      border: "2px solid #EF4444",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "50%",
                        padding: "8px",
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ fontSize: "24px" }}>🚫</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h2
                        style={{
                          color: "white",
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: "600",
                        }}
                      >
                        หมวดหมู่เกมส์ที่ไม่สามารถเล่นได้
                      </h2>
                      <p
                        style={{
                          color: "rgba(255, 255, 255, 0.9)",
                          margin: 0,
                          fontSize: "14px",
                          marginTop: "4px",
                        }}
                      >
                        เนื่องจากท่านติดเงื่อนไข โปร, เทิร์น หรือกฎของเว็บไซต์
                      </p>
                    </div>
                    <div
                      style={{
                        background: "rgba(255, 255, 255, 0.2)",
                        borderRadius: "20px",
                        padding: "6px 12px",
                        fontSize: "12px",
                        color: "white",
                        fontWeight: "500",
                      }}
                    >
                      ไม่อนุญาต
                    </div>
                  </div>
                </div>
              )}

              {/* Playable providers */}
              {isTypeAllowed
                ? (() => {
                    const playable = visibleProviders.filter(isProviderAllowed);

                    // Don't show playable section if user has ALLPROVIDER and allgametype
                    const hasAllAccess = !restrictionSummary.hasRestrictions;

                    if (playable.length > 0 && !hasAllAccess) {
                      return (
                        <div style={{ marginBottom: 32 }}>
                          {/* Enhanced Header for Playable Section */}
                          <div
                            className="playable-section"
                            style={{
                              background:
                                "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 90%, transparent) 0%, color-mix(in srgb, var(--primaryColor) 80%, transparent) 100%)",
                              borderRadius: "16px",
                              padding: "16px 24px",
                              marginBottom: 16,
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow:
                                "0 8px 25px color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                              border:
                                "2px solid color-mix(in srgb, var(--primaryColor) 60%, transparent)",
                            }}
                          >
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.2)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "40px",
                                height: "40px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ fontSize: "24px" }}>🎯</span>
                            </div>
                            <div
                              className="content"
                              style={{ flex: 1, textAlign: "center" }}
                            >
                              <h2
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "18px",
                                  fontWeight: "600",
                                }}
                              >
                                เกมส์ที่สามารถเล่นได้
                              </h2>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.9)",
                                  margin: 0,
                                  fontSize: "14px",
                                  marginTop: "4px",
                                }}
                              >
                                คลิกเพื่อเข้าเล่นได้เลย
                              </p>
                            </div>
                            <div
                              style={{
                                marginLeft: "auto",
                                background: "rgba(0, 0, 0, 0.2)",
                                borderRadius: "20px",
                                padding: "6px 12px",
                                fontSize: "12px",
                                color: "white",
                                fontWeight: "500",
                              }}
                            >
                              {playable.length} เกมส์
                            </div>
                          </div>

                          {/* Enhanced Grid for Playable Games */}
                          <div className="responsive-grid">
                            {playable.map((provider) => {
                              const src = resolveProviderImage(provider);
                              return (
                                <div
                                  key={provider.productId || provider.game}
                                  style={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "16px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    border:
                                      "3px solid color-mix(in srgb, var(--primaryColor) 50%, transparent)",
                                    position: "relative",
                                    overflow: "hidden",
                                    boxShadow:
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1.05) translateY(-4px)";
                                    e.currentTarget.style.boxShadow =
                                      "0 12px 30px color-mix(in srgb, var(--primaryColor) 40%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "var(--primaryColor)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.transform =
                                      "scale(1) translateY(0)";
                                    e.currentTarget.style.boxShadow =
                                      "0 4px 15px color-mix(in srgb, var(--primaryColor) 20%, transparent)";
                                    e.currentTarget.style.borderColor =
                                      "color-mix(in srgb, var(--primaryColor) 50%, transparent)";
                                  }}
                                  onClick={() =>
                                    loadProviderGamesWithCheck(provider)
                                  }
                                >
                                  {/* Success Badge */}
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "8px",
                                      right: "8px",
                                      background:
                                        "linear-gradient(135deg, var(--primaryColor), color-mix(in srgb, var(--primaryColor) 80%, #000))",
                                      borderRadius: "50%",
                                      width: "32px",
                                      height: "32px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      boxShadow:
                                        "0 2px 8px color-mix(in srgb, var(--primaryColor) 40%, transparent)",
                                      zIndex: 2,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "white",
                                      }}
                                    >
                                      ✓
                                    </span>
                                  </div>

                                  <img
                                    src={src}
                                    alt={provider.productName || provider.name}
                                    style={{
                                      width: "100%",
                                      objectFit: "contain",
                                      borderRadius: "12px 12px 0 0",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "0",
                                      left: "0",
                                      right: "0",
                                      background:
                                        "linear-gradient(to top, color-mix(in srgb, var(--primaryColor) 90%, transparent), color-mix(in srgb, var(--primaryColor) 70%, transparent))",
                                      color: "white",
                                      padding: "12px",
                                      borderRadius: "0 0 12px 12px",
                                    }}
                                  >
                                    <h3
                                      style={{
                                        fontSize: "14px",
                                        margin: "0",
                                        fontWeight: "600",
                                        textShadow:
                                          "0 1px 2px rgba(0, 0, 0, 0.3)",
                                      }}
                                    >
                                      {provider.productName || provider.name}
                                    </h3>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }
                    // Show locked only when there are no playable providers AND the user does not have all access
                    if (playable.length === 0 && !hasAllAccess) {
                      return (
                        <div style={{ marginBottom: 24, marginTop: 12 }}>
                          <div
                            className="restricted-section"
                            style={{
                              background:
                                "linear-gradient(135deg, #6B7280 0%, #4B5563 100%)",
                              borderRadius: "12px",
                              padding: "16px 20px",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              boxShadow: "0 6px 20px rgba(107, 114, 128, 0.25)",
                              border: "1px solid #6B7280",
                              position: "relative",
                              overflow: "hidden",
                            }}
                          >
                            {/* Animated background pattern */}
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  "repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255,255,255,0.03) 8px, rgba(255,255,255,0.03) 16px)",
                                animation: "slidePattern 4s linear infinite",
                              }}
                            />

                            {/* Icon container */}
                            <div
                              className="icon-container"
                              style={{
                                background: "rgba(255, 255, 255, 0.12)",
                                borderRadius: "50%",
                                padding: "8px",
                                width: "36px",
                                height: "36px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                position: "relative",
                                zIndex: 2,
                                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                              }}
                            >
                              <span style={{ fontSize: "18px" }}>🔒</span>
                            </div>

                            {/* Content */}
                            <div
                              className="content"
                              style={{
                                flex: 1,
                                position: "relative",
                                zIndex: 2,
                              }}
                            >
                              <h3
                                style={{
                                  color: "white",
                                  margin: 0,
                                  fontSize: "16px",
                                  fontWeight: "600",
                                  marginBottom: "4px",
                                  textShadow: "0 1px 3px rgba(0, 0, 0, 0.3)",
                                }}
                              >
                                ไม่สามารถเข้าถึงเกมส์ได้
                              </h3>
                              <p
                                style={{
                                  color: "rgba(255, 255, 255, 0.85)",
                                  margin: 0,
                                  fontSize: "13px",
                                  lineHeight: "1.4",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                ท่านไม่สามารถเล่นเกมส์ในหมวดหมู่นี้ได้
                                เนื่องจากติดเงื่อนไขการเล่น
                              </p>
                            </div>

                            {/* Status badge */}
                            <div
                              style={{
                                background: "rgba(255, 255, 255, 0.15)",
                                borderRadius: "16px",
                                padding: "4px 10px",
                                fontSize: "11px",
                                color: "white",
                                fontWeight: "500",
                                position: "relative",
                                zIndex: 2,
                                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.2)",
                                border: "1px solid rgba(255, 255, 255, 0.2)",
                              }}
                            >
                              จำกัด
                            </div>

                            <style>
                              {`
                            @keyframes slidePattern {
                              0% {
                                transform: translateX(-32px);
                              }
                              100% {
                                transform: translateX(0px);
                              }
                            }
                          `}
                            </style>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()
                : null}

              {/* All providers (render inline instead of ProviderCard) */}
              <div style={{ marginTop: 16 }}>
                <div className="responsive-grid">
                  {visibleProviders.length > 0 &&
                    visibleProviders.map((provider) => {
                      const src = resolveProviderImage(provider);
                      return (
                        <div
                          key={provider.productId || provider.game}
                          style={{
                            backgroundColor: "#ffffff",
                            borderRadius: "12px",
                            textAlign: "center",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            border:
                              "2px solid color-mix(in srgb, var(--primaryColor) 30%, transparent)",
                            position: "relative",
                            overflow: "hidden",
                            boxShadow:
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-4px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 20px color-mix(in srgb, var(--primaryColor) 35%, transparent)";
                            e.currentTarget.style.borderColor =
                              "var(--primaryColor)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px color-mix(in srgb, var(--primaryColor) 15%, transparent)";
                            e.currentTarget.style.borderColor =
                              "color-mix(in srgb, var(--primaryColor) 30%, transparent)";
                          }}
                          onClick={() => loadProviderGamesWithCheck(provider)}
                        >
                          <img
                            src={src}
                            alt={provider.productName || provider.name}
                            style={{
                              width: "100%",
                              objectFit: "contain",
                              borderRadius: "8px",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <h3
                            style={{
                              position: "absolute",
                              bottom: "0",
                              left: "0",
                              right: "0",
                              color: "white",
                              fontSize: "14px",
                              margin: "0",
                              padding: "4px 12px",
                              fontWeight: "600",
                              backgroundColor: "rgba(0, 0, 0, 0.7)",
                              borderRadius: "0 0 8px 8px",
                            }}
                          >
                            {provider.productName || provider.name}
                          </h3>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {selectedProvider && (
            <div>
              <div style={{ color: "#000000", marginBottom: 12 }}>
                ผู้ให้บริการ:{" "}
                {selectedProvider?.productName || selectedProvider?.name || "-"}
              </div>
              {isLoadingGames && (
                <div style={{ color: "#aaa" }}>กำลังโหลดรายการเกม...</div>
              )}
              {gamesError && (
                <div style={{ color: "#ef4444" }}>{gamesError}</div>
              )}
              {!isLoadingGames && !gamesError && (
                <>
                  <div className="responsive-grid">
                    {filteredProviderGames?.length ? (
                      filteredProviderGames.map((g) => (
                        <GameCard
                          key={
                            g.gameId ||
                            g.id ||
                            `${g.providerId || selectedProvider?.productId}-${
                              g.code || g.gameCode || g.gamecode || g.name
                            }`
                          }
                          game={g}
                          onClick={() => getGameLink(g)}
                        />
                      ))
                    ) : (
                      <div style={{ color: "#aaa" }}>
                        ไม่พบเกมสำหรับผู้ให้บริการนี้
                      </div>
                    )}
                  </div>
                  <GamePaginationControls
                    pagination={providerGamesPagination}
                    disabled={isLoadingGames}
                    onPageChange={handleGamesPageChange}
                  />
                </>
              )}
            </div>
          )}

          {gameStartLoading && <GameLaunchOverlay />}

          {/* Toast Notification */}
          <Toast
            message={toast.message}
            type={toast.type}
            show={toast.show}
            onClose={hideToast}
          />

          <style>{`
        .page-container{padding:24px}
        @media (max-width:768px){.page-container{padding:0}}
        @media (min-width:768px){.page-container{padding:32px}}
        .responsive-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:16px;margin-top:20px}
        @media (min-width:768px){.responsive-grid{grid-template-columns:repeat(3,1fr)}}
        @media (min-width:1200px){.responsive-grid{grid-template-columns:repeat(5,1fr)}}
        @media (max-width: 768px) {
          .games-page-shell {
            padding: 0 0 40px !important;
          }

          .header-section {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 16px !important;
          }
          
          .page-title {
            font-size: 24px !important;
            width: 100% !important;
            justify-content: center !important;
          }
          
          .back-button {
            width: 100% !important;
            text-align: center !important;
            padding: 12px 16px !important;
          }

          .search-button {
            align-self: flex-end !important;
          }
          
          .playable-section,
          .restricted-section {
            padding: 12px 16px !important;
            flex-direction: column !important;
            text-align: center !important;
            gap: 8px !important;
          }
          
          .playable-section .icon-container,
          .restricted-section .icon-container {
            width: 32px !important;
            height: 32px !important;
            padding: 6px !important;
          }
          
          .playable-section .content h2,
          .restricted-section .content h3 {
            font-size: 16px !important;
          }
          
          .playable-section .content p,
          .restricted-section .content p {
            font-size: 12px !important;
          }
        }
      `}</style>
        </div>
      </div>
    </div>
  );
};

export default Slot;
